import { computed } from "vue";
import { useStore } from "vuex";

/**
 * Composable for checking alert-related permissions
 *
 * Roles:
 * - alertsViewer: Can only view alerts (read-only)
 * - alertsManager: Can view, edit, and reanalyze alerts (full permissions)
 * - master/root: Have all permissions
 */
export function useAlertPermissions() {
  const store = useStore();

  /**
   * Get user's roles array
   */
  const userRoles = computed<string[]>(() => {
    const roles = store.getters["auth/getUserRole"];
    return Array.isArray(roles) ? roles : [];
  });

  /**
   * Check if user has alertsManager role or equivalent
   * (can edit and reanalyze alerts)
   */
  const hasAlertsManager = computed(() => {
    return userRoles.value.some((role) =>
      ["alertsManager", "master", "root"].includes(role)
    );
  });

  /**
   * Check if user has alertsViewer role or better
   * (can view alerts)
   */
  const hasAlertsViewer = computed(() => {
    return hasAlertsManager.value || userRoles.value.includes("alertsViewer");
  });

  /**
   * Check if user can edit alerts
   */
  const canEditAlerts = computed(() => hasAlertsManager.value);

  /**
   * Check if user can reanalyze/reprocess alerts
   */
  const canReanalyzeAlerts = computed(() => hasAlertsManager.value);

  /**
   * Check if user can link messages manually
   */
  const canLinkMessages = computed(() => hasAlertsManager.value);

  return {
    userRoles,
    hasAlertsManager,
    hasAlertsViewer,
    canEditAlerts,
    canReanalyzeAlerts,
    canLinkMessages,
  };
}
